/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-24
 */

// Add third-party dependencies.
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import { SubscriptionPlan } from '../../types/subscription';

/**
 * Props for the DowngradeViolationDialog component.
 */
interface DowngradeViolationDialogProps {
  /**
   * Whether the dialog is open.
   */
  open: boolean;
  /**
   * Callback when the dialog should be closed.
   */
  onClose: () => void;
  /**
   * The plan the user is trying to downgrade to.
   */
  targetPlan: SubscriptionPlan | null;
  /**
   * Array of violation messages from the backend.
   */
  violations: string[];
}

/**
 * Dialog component for displaying downgrade violations.
 * Shows users exactly what needs to be resolved before they can downgrade.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The downgrade violation dialog component
 */
const DowngradeViolationDialog: React.FC<DowngradeViolationDialogProps> = ({
  open,
  onClose,
  targetPlan,
  violations,
}) => {
  const { t } = useTranslation('billing');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="downgrade-violation-dialog-title"
    >
      <DialogTitle id="downgrade-violation-dialog-title">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            {t('downgrade.cannotDowngrade', {
              plan: targetPlan?.name || 'Selected Plan',
            })}
          </Typography>
          <Button onClick={onClose} sx={{ minWidth: 'auto' }}>
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('downgrade.usageExceeds', {
              plan: targetPlan?.name || 'selected',
            })}
          </Typography>
        </Alert>

        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: 'bold' }}
        >
          {t('downgrade.issuesToResolve')}
        </Typography>

        <List>
          {violations.map(violation => (
            <ListItem key={violation} disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <WarningIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {violation}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('downgrade.whatToDo')}</strong>{' '}
            {t('downgrade.whatToDoDescription')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('downgrade.fixIssues')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DowngradeViolationDialog;
